// =====================================================================
// Assessment Center — Instructor Panel
// Grading page — attempts waiting on essay review. Only exams with an
// essay section ever land here: Multiple Choice exams are graded (and
// never show up in this queue) the instant a student submits.
// =====================================================================
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, EmptyState, PageHeader, Spinner } from "../../../shared/components/ui";
import { listPendingReviews, type PendingReviewAttempt } from "../lib/grading-api";

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function GradingPage() {
  const [items, setItems] = useState<PendingReviewAttempt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listPendingReviews().then((data) => {
      setItems(data);
      setLoading(false);
    });
  }, []);

  return (
    <div>
      <PageHeader
        title="Grading"
        subtitle="Essay answers waiting for review. Multiple choice exams are graded automatically."
      />

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-slate-500">
          <Spinner /> Loading submissions…
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="Nothing to review" description="Every submitted essay answer has been graded." />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-navy-50/60 text-xs uppercase tracking-wide text-navy-700">
              <tr>
                <th className="px-4 py-3 font-medium">Exam</th>
                <th className="px-4 py-3 font-medium">Student</th>
                <th className="px-4 py-3 font-medium">Attempt</th>
                <th className="px-4 py-3 font-medium">Submitted</th>
                <th className="px-4 py-3 font-medium">Essays pending</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.attempt_id} className="border-t border-border-soft hover:bg-navy-50/30">
                  <td className="px-4 py-3 font-medium text-navy-900">{item.exam_title}</td>
                  <td className="px-4 py-3 text-slate-500">{item.student_id}</td>
                  <td className="px-4 py-3 text-slate-500">#{item.attempt_number}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(item.submitted_at)}</td>
                  <td className="px-4 py-3">
                    <Badge tone="amber">
                      {item.pending_essay_count} / {item.total_essay_count}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/instructor/assessment-center/grading/${item.attempt_id}`}
                      className="text-sm font-medium text-navy-700 hover:underline"
                    >
                      Review
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
